/**
 * "Recent" carousel for the mobile header — a horizontal snap strip merging
 * recently-OPENED workspaces (workspaceApi.listRecent, per-email for orgs)
 * with the viewer's own recently-CHANGED files (#653: the Recent data, groups
 * flattened to files — an app has none) and the files opened on this device.
 * Renders nothing while empty so the header stays compact.
 */
import { useEffect, useRef, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { LayoutGrid } from 'lucide-react'
import { FileIcon } from '@papan/core-ui/ui'
import { useRecentWorkPreview } from '@papan/files/client/queries'
import { flattenRecentFiles, useRecentWords } from '@papan/files/ui'
import { workspaceApi, getWorkspaceName } from '@papan/workspace/client'
import { disambiguateParentLabels } from '@papan/types'
import { useTenant } from '../hooks/useTenant'
import { readRecentOpens, recordFileOpen, onRecentOpensChanged, type RecentOpen } from './recentOpens'

interface RecentCard {
  key: string
  kind: 'workspace' | 'file'
  /** Navigation target id — workspace cards carry the FOLDER id. */
  id: string
  name: string
  /** Immediate parent folder (file cards) — for same-name disambiguation. */
  parentName?: string | null
  /** epoch ms of the latest activity (open OR modify) — sort key. */
  at: number
}

export function MobileRecents() {
  const navigate = useNavigate()
  const location = useLocation()
  const { tenantId, isPersonal, activeOrgEmail } = useTenant()
  const prefix = isPersonal ? '/~' : `/t/${tenantId}`

  const recentWork = useRecentWorkPreview('mine')
  const recentWorkspaces = useQuery({
    queryKey: ['mobile-recent-workspaces', tenantId, activeOrgEmail ?? ''],
    queryFn: () => workspaceApi.listRecent(),
    staleTime: 30_000,
  })

  // Per-device "recently opened" files (localStorage), scoped per identity.
  // The server list is recently-MODIFIED only, so this is what makes OPENING a
  // file bump it to the front.
  const scope = `${tenantId}:${activeOrgEmail ?? ''}`
  const [opens, setOpens] = useState<RecentOpen[]>(() => readRecentOpens(scope))
  useEffect(() => {
    setOpens(readRecentOpens(scope))
    return onRecentOpensChanged(() => setOpens(readRecentOpens(scope)))
  }, [scope])

  // When a NEW open lands at the front (from here OR the drive listing), the
  // reordered card is at the far left — scroll the strip back so it's actually
  // visible instead of leaving the bar parked wherever it was.
  const stripRef = useRef<HTMLDivElement>(null)
  const lastOpenAt = useRef(opens[0]?.at ?? 0)
  // Mouse-drag scroll state (kept above the early-return so hook order is
  // stable whether or not there are cards).
  const drag = useRef<{ x: number; scroll: number; moved: boolean; id: number } | null>(null)
  const suppressClick = useRef(false)
  useEffect(() => {
    const top = opens[0]?.at ?? 0
    if (top > lastOpenAt.current) {
      lastOpenAt.current = top
      stripRef.current?.scrollTo({ left: 0, behavior: 'smooth' })
    }
  }, [opens])

  // Merge modified (server) + opened (device) files by id; each file's sort
  // time is the LATEST of the two, so opening OR editing surfaces it.
  const fileAt = new Map<string, { name: string; parentName: string | null; at: number }>()
  const recentWords = useRecentWords(activeOrgEmail, undefined)
  for (const f of flattenRecentFiles(recentWork.data?.groups ?? [], 10, recentWords)) {
    fileAt.set(f.id, { name: f.name, parentName: f.placeName, at: Date.parse(f.at) || 0 })
  }
  for (const o of opens) {
    const ex = fileAt.get(o.id)
    fileAt.set(o.id, {
      name: o.name || ex?.name || o.id,
      // The server's folder name first: it reads "My Space" for the viewer's own space, a saved one is raw.
      parentName: ex?.parentName ?? o.parentName ?? null,
      at: Math.max(o.at, ex?.at ?? 0),
    })
  }

  const cards: RecentCard[] = [
    ...(recentWorkspaces.data ?? []).map((w): RecentCard => ({
      key: `ws:${w.id}`,
      kind: 'workspace',
      id: w.folderId,
      name: getWorkspaceName(w.name),
      at: Date.parse(w.updatedAt ?? '') || 0,
    })),
    ...[...fileAt].map(([id, v]): RecentCard => ({
      key: `file:${id}`,
      kind: 'file',
      id,
      name: v.name,
      parentName: v.parentName,
      at: v.at,
    })),
  ]
    .sort((a, b) => b.at - a.at)
    .slice(0, 12)

  if (cards.length === 0) return null

  // VS-Code-style same-name disambiguation: only files whose name collides with
  // ANOTHER visible file card get a folder suffix (e.g. index.html/src vs
  // index.html/demo). Uses the shared disambiguateParentLabels over one-level
  // pseudo-paths built from the immediate parent name.
  const fileCards = cards.filter((c) => c.kind === 'file')
  const nameCount = new Map<string, number>()
  for (const c of fileCards) nameCount.set(c.name, (nameCount.get(c.name) ?? 0) + 1)
  const labels = disambiguateParentLabels(fileCards.map((c) => `/${c.parentName ?? ''}/${c.name}`))
  const folderLabel = new Map<string, string>()
  fileCards.forEach((c, i) => {
    if ((nameCount.get(c.name) ?? 0) > 1 && labels[i]) folderLabel.set(c.id, labels[i])
  })

  // ── Mouse / pen drag-to-scroll + wheel → horizontal ──────────────────
  // overflow-x-auto only responds to TOUCH + trackpad, so on a desktop a mouse
  // can't scroll the strip. Fingers keep native momentum (we bail on
  // pointerType 'touch'); mouse/pen drive scrollLeft directly, then a moved
  // drag suppresses the click it synthesizes so a drag never opens a card.
  const onPointerDown = (e: React.PointerEvent) => {
    if (e.pointerType === 'touch') return
    const strip = stripRef.current
    if (!strip) return
    suppressClick.current = false
    drag.current = { x: e.clientX, scroll: strip.scrollLeft, moved: false, id: e.pointerId }
  }
  const onPointerMove = (e: React.PointerEvent) => {
    const d = drag.current
    const strip = stripRef.current
    if (!d || !strip) return
    const dx = e.clientX - d.x
    if (!d.moved) {
      if (Math.abs(dx) <= 4) return // still a click, not a drag
      d.moved = true
      try { strip.setPointerCapture(d.id) } catch { /* not capturable */ }
    }
    strip.scrollLeft = d.scroll - dx
  }
  const endPointer = () => {
    const d = drag.current
    const strip = stripRef.current
    if (!d || !strip) return
    drag.current = null
    try { strip.releasePointerCapture(d.id) } catch { /* already released */ }
    if (d.moved) suppressClick.current = true // swallow the drag-release click
  }
  // Plain vertical wheel → horizontal scroll; trackpads emitting deltaX keep
  // their native h-scroll.
  const onWheel = (e: React.WheelEvent) => {
    const strip = stripRef.current
    if (!strip) return
    if (Math.abs(e.deltaY) > Math.abs(e.deltaX)) strip.scrollLeft += e.deltaY
  }

  const open = (c: RecentCard) => {
    if (suppressClick.current) { suppressClick.current = false; return } // was a drag
    // Workspace cards carry the folderId — the workspace route's param.
    if (c.kind === 'workspace') { navigate(`${prefix}/workspace/${c.id}`); return }
    // Record the open so this file jumps to the front of Recent immediately.
    recordFileOpen(scope, c.id, c.name, c.parentName)
    // One-file-at-a-time: opening a file while ALREADY viewing one replaces it
    // (the old file "closes") instead of stacking — so the drive never grows a
    // pile of files the user has to close one by one.
    const inFile = /\/file\//.test(location.pathname)
    navigate(`${prefix}/file/${c.id}`, { replace: inFile })
  }

  return (
    <div className="min-h-0">
      <p className="px-4 pb-1 text-[11px] uppercase tracking-wide themed-text-muted">Recent</p>
      <div
        ref={stripRef}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={endPointer}
        onPointerCancel={endPointer}
        onWheel={onWheel}
        // scroll-px-4: cards snap to the 16px gutter (the label's), not the screen edge.
        className="flex gap-2 overflow-x-auto px-4 scroll-px-4 pb-2 snap-x cursor-grab active:cursor-grabbing [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
      >
        {cards.map((c) => {
          const label = c.kind === 'file' ? folderLabel.get(c.id) : undefined
          return (
            <button
              key={c.key}
              onClick={() => open(c)}
              className="flex snap-start items-center gap-2 rounded-lg border themed-border themed-bg-secondary px-3 py-2 shrink-0 max-w-[11rem] active:themed-bg-tertiary"
            >
              {c.kind === 'workspace'
                ? <LayoutGrid size={16} className="shrink-0 text-purple-400" />
                : <FileIcon name={c.name} size={16} />}
              <span className="truncate text-xs themed-text">
                {c.name}{label && <span className="ml-1 text-[10px] themed-text-muted">/{label}</span>}
              </span>
            </button>
          )
        })}
      </div>
    </div>
  )
}
